<script lang="ts" setup>
import RecommendationBadge from '@/components/badges/RecommendationBadge.vue'
import { copyToClipboard, formatPrice, writeJsonToClipboard } from '@/helper/tools.ts'
import { CharacteristicType, RecommendationBadgeType } from '@/helper/Types.ts'
import userStore from '@/store/UserStore.ts'
import SpinnerInput from '@/components/steps/accessories/SpinnerInput.vue'
import EnlargeBox from '@/components/EnlargeBox.vue'
import { computed, watch } from 'vue'
import { Article } from '@/services/models/Article.ts'
import formStore from '@/store/FormStore.ts'
import { AccessoryCharacteristic } from '@/services/models/AccessoryCharacteristic.ts'

const props = defineProps<{
  article: Article
  group: string
  enlargeBoxId: string
}>()

defineEmits<{
  (e: 'showDetails', id: string): void
}>()

const accessory = computed(() => props.article.details)
const minSpinnerValue = computed(() => {
  if (accessory.value?.inSalePackage) {
    const characteristic = accessory.value.getProductCharacteristic(CharacteristicType.ACCESSORY) as AccessoryCharacteristic

    return characteristic.inSaleSetQuantity ?? 0
  }
  return 0
})

const maxSpinnerValue = computed(() => {
  if (accessory.value?.inSalePackage && accessory.value.price === 0) {
    const characteristic = accessory.value.getProductCharacteristic(CharacteristicType.ACCESSORY) as AccessoryCharacteristic

    return characteristic.inSaleSetQuantity ?? 99
  }
  return 99
})

watch(() => props.article.quantity, () => formStore.data.missingAccessoriesConfirmed = false)
</script>

<template>
  <div
    v-if="accessory"
    :data-id="accessory.id"
    class="fTable__item"
    @click.exact.alt.prevent="copyToClipboard(accessory.id)"
    @click.exact.alt.ctrl.shift.prevent="writeJsonToClipboard(accessory)"
  >
    <div class="fTable__tr has--td">
      <RecommendationBadge v-if="accessory.recommended" :badge-type="RecommendationBadgeType.LEFT" />

      <div class="fTable__td column is--product">
        <div class="fTable__thumbnail">
          <div class="fTable__image-box image-box">
            <div :class="!accessory.pictureUrl && 'fallback-bg'" class="image-box__container">
              <img
                v-if="accessory.pictureUrl"
                :alt="accessory.name"
                :src="accessory.pictureUrl"
                loading="lazy"
              >
            </div>
          </div>
        </div>
        <div class="fTable__product-infos">
          <span>{{ accessory.name }}</span>
          <span class="is--component-spec hide-desktop">
            <b>{{ $t('step_accessories.table_order_number') }}:&nbsp;</b> {{ accessory.orderNumber }}
          </span>
          <span v-if="!userStore.hidePrices" class="is--component-spec hide-desktop">
            <b>{{ $t('step_accessories.table_single_price') }}:</b>
            {{ formatPrice(accessory.price) }}
          </span>
        </div>
      </div>

      <div class="fTable__td column is--order-number hide-mobile">
        <span>{{ accessory.orderNumber }}</span>
      </div>

      <div v-if="!userStore.hidePrices" class="fTable__td column is--price hide-mobile">
        <span>{{ formatPrice(accessory.price) }}</span>
      </div>

      <div class="fTable__td column is--details hide-mobile">
        <button
          v-if="accessory.productAdvantages && accessory.productAdvantages.length > 0"
          class="btn is--raw fTable__details-btn"
          type="button"
          @click="$emit('showDetails',`accessory-${group}-details${accessory.id}`)"
        >
          {{ $t('step_accessories.table_details') }}
          <i class="ic-chevron-down" />
        </button>
      </div>

      <div class="fTable__td column is--spinner-controls">
        <SpinnerInput
          :id="`accessory-${accessory.id}`"
          v-model="article.quantity"
          :hide-controls-mobile="true"
          :max-val="maxSpinnerValue"
          :min-val="minSpinnerValue"
        />
        <button
          v-if="accessory.productAdvantages && accessory.productAdvantages.length > 0"
          class="btn is--raw fTable__details-btn hide-desktop"
          type="button"
          @click="$emit('showDetails',`accessory-${group}-details${accessory.id}`)"
        >
          {{ $t('step_accessories.table_details') }}
          <i class="ic-chevron-down" />
        </button>
      </div>
    </div>

    <EnlargeBox
      v-if="accessory.productAdvantages && accessory.productAdvantages.length > 0"
      :ref="`accessory-${group}-details${accessory.id}`"
      :is-open="enlargeBoxId === `accessory-${group}-details${accessory.id}`"
      class="fTable__tr fTable__details details"
    >
      <template #content>
        <div class="fTable__td column is--product hide-mobile">
          <div class="image-box details__image-box">
            <div :class="!accessory.pictureUrl && 'fallback-bg'" class="image-box__container">
              <img
                v-if="accessory.pictureUrl"
                :alt="accessory.name"
                :src="accessory.pictureUrl"
                loading="lazy"
              >
            </div>
          </div>
        </div>
        <div class="column">
          <ul class="check-list product__features">
            <li v-for="number in Math.min(3, accessory.productAdvantages.length)" :key="number">
              {{ accessory.productAdvantages[number - 1] }}
            </li>
          </ul>
        </div>
      </template>
    </EnlargeBox>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.fTable__item {
  overflow: visible;

  .fTable__product-infos {
    @include media('<tablet') {
      padding: .5rem;
    }
  }
}

</style>
